import { getSingleValueForTasField } from 'tio-common/utils/tuition-assistance/fields';
import type { FieldValue } from 'tio-common/utils/tuition-assistance/fields';
import type { TASApplicationModelFieldsSignature } from 'tio-data/data/resources/tas-application.type';

/**
 * The guided wizard's submit pipeline, as data + a dependency-injected runner.
 * The ordering is the contract — the gate runs before anything persists, and
 * the persisted totals are verified before the state transition fires — so it
 * lives here where it can be unit-tested without mounting the wizard.
 */

/** The application fields the submit-time save stamps with money amounts. */
export type TotalsField =
  | 'PRE_APPROVED_TOTAL'
  | 'FINAL_APPROVED_TOTAL'
  | 'CARD_PRE_APPROVED_TOTAL'
  | 'CARD_FINAL_APPROVED_TOTAL';

export interface TotalsExpectation {
  field: TotalsField;
  /** Cents the submit-time save stamped; null when the save cleared the field. */
  expected: number | null;
}

/**
 * What the submit-time save is expected to have persisted, mirroring the
 * wizard's stamping logic: a CourseCa$h direct-pay submit splits the request
 * across a card field and a non-card field, every other submit writes the
 * whole amount to one field and clears both card fields. Type 2 programs use
 * the FINAL_* fields, everything else the PRE_* fields.
 */
export function buildTotalsExpectations(params: {
  /** True for a direct-pay program paying via CourseCa$h. */
  usesCourseCashSplit: boolean;
  isTypeTwoProgram: boolean;
  /** Cents routed to the CourseCa$h card (already net of scholarships). */
  courseCashSubtotal: number;
  /** Cents paid outside the card. */
  nonCourseCashSubtotal: number;
  /** Cents for the whole request when no card split applies. */
  requestedTotalAmount: number;
}): TotalsExpectation[] {
  const totalField = params.isTypeTwoProgram ? 'FINAL_APPROVED_TOTAL' : 'PRE_APPROVED_TOTAL';
  if (params.usesCourseCashSplit) {
    return [
      {
        field: params.isTypeTwoProgram ? 'CARD_FINAL_APPROVED_TOTAL' : 'CARD_PRE_APPROVED_TOTAL',
        expected: params.courseCashSubtotal,
      },
      { field: totalField, expected: params.nonCourseCashSubtotal },
    ];
  }
  return [
    { field: 'CARD_PRE_APPROVED_TOTAL', expected: null },
    { field: 'CARD_FINAL_APPROVED_TOTAL', expected: null },
    { field: totalField, expected: params.requestedTotalAmount },
  ];
}

/**
 * Compares the totals actually on a committed fields hash against what the
 * save was expected to stamp, returning the first expectation that does not
 * hold (or undefined when the write landed). A save request that silently
 * omits `fields` gets a 200 whose response body carries the old totals, so the
 * committed record — not the local editing copy — is what must be checked.
 * An expected amount of 0 is a real value: a missing/null persisted field does
 * not satisfy it.
 */
export function findPersistedTotalsMismatch(
  fields: TASApplicationModelFieldsSignature | undefined,
  expectations: TotalsExpectation[]
): TotalsExpectation | undefined {
  return expectations.find(({ field, expected }) => {
    const persisted = getSingleValueForTasField<
      TASApplicationModelFieldsSignature,
      FieldValue | undefined
    >(field, fields);
    const isEmpty = persisted === null || persisted === undefined || persisted === '';
    if (expected === null) {
      return !isEmpty;
    }
    return isEmpty || Number(persisted) !== expected;
  });
}

export type GuidedSubmitOutcome = 'blocked' | 'totals-mismatch' | 'submitted' | 'errored';

/**
 * Runs the submit pipeline in its load-bearing order: the submit gate first
 * (a blocked submit persists nothing), then the totals save, then verification
 * that the write landed — only then the agreements save and the state
 * transition. Each abort path invokes its callback so the caller can surface a
 * user-visible error; any thrown error routes to `onError`.
 */
export async function runGuidedSubmitFlow(hooks: {
  canSubmit: () => boolean;
  persistTotals: () => Promise<void>;
  findTotalsMismatch: () => TotalsExpectation | undefined;
  saveAgreements: () => Promise<unknown>;
  submit: () => Promise<unknown>;
  onBlocked: () => void;
  onTotalsMismatch: (mismatch: TotalsExpectation) => void;
  onError: (error: unknown) => void;
}): Promise<GuidedSubmitOutcome> {
  try {
    if (!hooks.canSubmit()) {
      hooks.onBlocked();
      return 'blocked';
    }
    await hooks.persistTotals();
    const mismatch = hooks.findTotalsMismatch();
    if (mismatch) {
      hooks.onTotalsMismatch(mismatch);
      return 'totals-mismatch';
    }
    await hooks.saveAgreements();
    await hooks.submit();
    return 'submitted';
  } catch (error) {
    hooks.onError(error);
    return 'errored';
  }
}
